"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface AuroraProps {
  className?: string;
  colors?: string[];
  speed?: number;
  blur?: number;
  intensity?: number;
}

export function Aurora({
  className = "",
  colors = ["#7c3aed", "#2563eb", "#06b6d4", "#10b981", "#f59e0b"],
  speed = 1,
  blur = 60,
  intensity = 0.6
}: AuroraProps) {
  const reducedMotion = useReducedMotion();

  if (reducedMotion) {
    return (
      <div className={`absolute inset-0 ${className}`}>
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${colors[0]}40, ${colors[1]}40, ${colors[2]}40)`,
          }}
        />
      </div>
    );
  }

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Large aurora waves */}
      {colors.map((color, index) => (
        <motion.div
          key={index}
          className="absolute"
          style={{
            width: `${80 + index * 15}%`,
            height: `${80 + index * 15}%`,
            background: `radial-gradient(ellipse at center, ${color}${Math.floor(intensity * 99).toString(16).padStart(2, '0')} 0%, transparent 60%)`,
            filter: `blur(${blur}px)`,
            left: `${-20 + (index * 25) % 50}%`,
            top: `${-20 + (index * 20) % 40}%`,
          }}
          animate={{
            x: [0, 100 * (index % 2 === 0 ? 1 : -1), -50, 0],
            y: [0, -80 * (index % 2 === 0 ? -1 : 1), 40, 0],
            scale: [1, 1.3, 0.9, 1],
            rotate: [0, index % 2 === 0 ? 15 : -15, 0],
          }}
          transition={{
            duration: (15 + index * 3) / speed,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Floating orbs for extra depth */}
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={`orb-${i}`}
          className="absolute rounded-full"
          style={{
            width: 200 + i * 100,
            height: 200 + i * 100,
            background: `radial-gradient(circle, ${colors[i % colors.length]}50 0%, transparent 70%)`,
            filter: `blur(${40 + i * 10}px)`,
            left: `${10 + i * 18}%`,
            top: `${20 + (i * 15) % 60}%`,
          }}
          animate={{
            y: [0, -30, 30, 0],
            x: [0, 20, -20, 0],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 8 + i * 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.5,
          }}
        />
      ))}

      {/* Gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#252423]/30 to-[#252423]" />
    </div>
  );
}

export default Aurora;
